import { Checkbox, Typography } from '../../components/primitives';
import { CheckoutTermsAndConditions } from '../../components/text/CheckoutTermsAndConditions';
import Alert from '../../components/ui/Alert';
import { useCheckoutContext } from '../../context/checkout.context';
import { BrainTreeComponent } from '../../hooks/braintreeHook';
import { testId } from '../../utils';
import { CheckoutAddress } from './checkout-address';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import React from 'react';

export const PaymentOptions: React.FC = () => {
  const {
    braintreeError,
    checkoutData,
    selectedShippingAddress,
    billingAddressRequired,
    setBillingAddressState,
    setSelectedBillingAddress,
    selectedBillingAddress,
    addBillingAddressForm,
    editBillingAddressForm,
  } = useCheckoutContext();
  const { t } = useTranslation(undefined, { keyPrefix: 'route.checkout' });
  const [billingSameAsShipping, setBillingSameAsShipping] =
    React.useState<boolean>(false);

  const selectedShippingAddressId = selectedShippingAddress?.id;
  const onSelectAddress = React.useCallback(
    (address: Cornerstone.ValueAddress) => {
      setBillingAddressState('SELECT');
      setSelectedBillingAddress(address);
      if (address.id === selectedShippingAddressId) {
        setBillingSameAsShipping(true);
      }
    },
    [
      setBillingAddressState,
      setSelectedBillingAddress,
      selectedShippingAddressId,
    ],
  );

  const onAddAddress = React.useCallback(() => {
    setBillingAddressState('ADD');
  }, [setBillingAddressState]);

  const onEditAddress = React.useCallback(() => {
    setBillingAddressState('EDIT');
  }, [setBillingAddressState]);

  const onBillingSameAsShipping = React.useCallback(
    (checked: boolean) => {
      if (checked) {
        setBillingAddressState('SELECT');
        setBillingSameAsShipping(true);
        setSelectedBillingAddress(selectedShippingAddress);
      } else {
        setBillingSameAsShipping(false);
        setSelectedBillingAddress(undefined);
      }
    },
    [
      selectedShippingAddress,
      setBillingAddressState,
      setSelectedBillingAddress,
    ],
  );

  if (!checkoutData) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <BrainTreeComponent />
      {braintreeError && (
        <Alert {...testId('payment-error')} variant="error">
          {t('payment_error')}
        </Alert>
      )}
      {billingAddressRequired && (
        <>
          <Typography variant="body-semibold">
            {t('billing_address')}
          </Typography>

          <div className="flex gap-2">
            <Checkbox
              {...testId('billing-address', 'same-as-shipping')}
              checked={billingSameAsShipping}
              onCheckedChange={onBillingSameAsShipping}
            />
            {t('same_as_shipping')}
          </div>
          {(!selectedBillingAddress || !billingSameAsShipping) && (
            <CheckoutAddress
              {...testId('billing-address')}
              noDefault
              selectedAddress={selectedBillingAddress}
              onSelect={onSelectAddress}
              onAdd={onAddAddress}
              onEdit={onEditAddress}
              addAddressForm={addBillingAddressForm}
              editAddressForm={editBillingAddressForm}
            />
          )}
        </>
      )}
      <div className="md:hidden">
        <CheckoutTermsAndConditions />
      </div>
    </div>
  );
};
